import { raceOfSkinKey } from "./attackdata";
import { creatureModelMatrix } from "./creaturemesh";

/** `NRaceData::ECollisionType` (`GameType.h:95-103`). */
export const COLLISION_TYPE_BODY = 1;
export const COLLISION_TYPE_ATTACKING = 2;
export const COLLISION_TYPE_DEFENDING = 3;

export const COLLISION_VOLUMES_URL = "creaturemesh/collision.json";

/** One sphere of a collision block, in millimetres in its bone's (or the
 *  actor's) frame. */
export interface CollisionSphereRow {
  readonly radiusMm: number;
  readonly positionMm: readonly number[];
}

/** One `AttachingDataType 1` block of a body's `.msm`, as
 *  `tools/build_actor_collision.py` writes it. */
export interface CollisionVolumeRow {
  readonly type: number;
  readonly attaching: boolean;
  readonly bone: string;
  readonly spheres: readonly CollisionSphereRow[];
}

/** `collision.json`: player rows by race id, every other body's by species
 *  name, and which race each player skin key draws. */
export interface CollisionVolumeTable {
  readonly racePrefixes: Readonly<Record<string, number>>;
  readonly races: Readonly<Record<string, readonly CollisionVolumeRow[]>>;
  readonly species: Readonly<Record<string, readonly CollisionVolumeRow[]>>;
}

/** The two lists an actor keeps: `m_BodyPointInstanceList` and
 *  `m_DefendingPointInstanceList`. */
export interface ActorVolumes {
  readonly body: readonly CollisionVolumeRow[];
  readonly defending: readonly CollisionVolumeRow[];
}

/** `CreatureMeshRenderer.boneAnchor`'s answer: a posed bone's world position and
 *  its three unit axes, column-major. */
export interface BoneAnchor {
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
  readonly basis: ArrayLike<number>;
}

/** Where the body stands and faces this frame. */
export interface BodyPlacement {
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
  readonly heading: number;
}

/** `CDynamicSphereInstance`: a sphere's world centre this frame and the one
 *  before it. */
export interface PlacedSphere {
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
  readonly lastXMm: number;
  readonly lastYMm: number;
  readonly lastZMm: number;
  readonly radiusMm: number;
}

/** One `TCollisionPointInstance`, placed. */
export interface PlacedVolume {
  readonly row: CollisionVolumeRow;
  readonly spheres: readonly PlacedSphere[];
}

const EMPTY: CollisionVolumeTable = { racePrefixes: {}, races: {}, species: {} };

/** A parsed `collision.json`, or an empty table for a body that holds none. */
export function collisionVolumeTable(body: unknown): CollisionVolumeTable {
  const table = body as Partial<CollisionVolumeTable> | null;
  return {
    racePrefixes: table?.racePrefixes ?? {},
    races: table?.races ?? {},
    species: table?.species ?? {},
  };
}

/** The collision blocks of the `.msm` `key`'s body is loaded from. */
export function collisionRowsFor(table: CollisionVolumeTable, key: string): readonly CollisionVolumeRow[] {
  const race = raceOfSkinKey(table.racePrefixes, key);
  return (race !== null ? table.races[String(race)] : table.species[key]) ?? [];
}

/** `RefreshActorInstance`'s collision half (`ActorInstanceAttach.cpp:312-349`):
 *  both lists cleared (:314-316), every attacking block skipped (:328-329), a
 *  defending block kept for defence (:337-339) and a body block for the body
 *  (:340-342). A body's item parts add nothing: no item loads attaching data
 *  (`ItemData.cpp:139-146`). */
export function refreshActorVolumes(rows: readonly CollisionVolumeRow[]): ActorVolumes {
  const body: CollisionVolumeRow[] = [];
  const defending: CollisionVolumeRow[] = [];
  for (const row of rows) {
    if (row.type === COLLISION_TYPE_ATTACKING) continue; // ActorInstanceAttach.cpp:328-329
    if (row.type === COLLISION_TYPE_DEFENDING) defending.push(row); // :337-339
    else if (row.type === COLLISION_TYPE_BODY) body.push(row); // :340-342
  }
  return { body, defending };
}

/** `UpdatePointInstance` (`ActorInstanceCollisionDetection.cpp:33-99`): each
 *  sphere's position composed into its bone's world transform when the block
 *  is attached, into the actor's world matrix when it is not (:74-77), the
 *  previous centre kept as the last one (:95-98). `boneAt` answers a posed
 *  bone; a bone this client did not pose places the sphere on the actor's world
 *  matrix. A first placement starts from a zero last centre, as
 *  `CreateCollisionInstancePiece` seeds it (`:248-250`). */
export function placeVolumes(
  volumes: readonly CollisionVolumeRow[],
  body: BodyPlacement,
  boneAt: (bone: string) => BoneAnchor | null,
  previous: readonly PlacedVolume[] = [],
): PlacedVolume[] {
  const world = creatureModelMatrix(body.xMm, body.yMm, body.zMm, body.heading);
  return volumes.map((row, volume) => {
    const anchor = row.attaching ? boneAt(row.bone) : null;
    const spheres = row.spheres.map((sphere, index): PlacedSphere => {
      const [px, py, pz] = sphere.positionMm;
      const last = previous[volume]?.row === row ? previous[volume].spheres[index] : undefined;
      const centre = anchor !== null
        ? {
          xMm: anchor.basis[0] * px + anchor.basis[3] * py + anchor.basis[6] * pz + anchor.xMm,
          yMm: anchor.basis[1] * px + anchor.basis[4] * py + anchor.basis[7] * pz + anchor.yMm,
          zMm: anchor.basis[2] * px + anchor.basis[5] * py + anchor.basis[8] * pz + anchor.zMm,
        }
        : {
          xMm: world[0] * px + world[4] * py + world[8] * pz + world[12],
          yMm: world[1] * px + world[5] * py + world[9] * pz + world[13],
          zMm: world[2] * px + world[6] * py + world[10] * pz + world[14],
        };
      return {
        ...centre,
        lastXMm: last?.xMm ?? 0,
        lastYMm: last?.yMm ?? 0,
        lastZMm: last?.zMm ?? 0,
        radiusMm: sphere.radiusMm,
      };
    });
    return { row, spheres };
  });
}

/** `CActorInstance::IntersectDefendingSphere` (`ActorInstance.cpp:753-782`):
 *  whether the pick ray's line passes within any defending sphere's radius.
 *  `rayDir` is unit length, as `ms_Ray`'s direction is; the test is on the
 *  whole line, so a sphere behind the origin answers too (:775-778). */
export function intersectDefendingSphere(
  defending: readonly PlacedVolume[],
  rayOrigin: readonly [number, number, number],
  rayDir: readonly [number, number, number],
): boolean {
  for (const volume of defending) {
    for (const sphere of volume.spheres) {
      const dx = rayOrigin[0] - sphere.xMm; // ActorInstance.cpp:773
      const dy = rayOrigin[1] - sphere.yMm;
      const dz = rayOrigin[2] - sphere.zMm;
      const b = rayDir[0] * dx + rayDir[1] * dy + rayDir[2] * dz; // :774
      const c = dx * dx + dy * dy + dz * dz - sphere.radiusMm * sphere.radiusMm; // :775
      if (b * b - c >= 0) return true; // :777-778
    }
  }
  return false;
}

/** The table, or an empty one when the file is absent or unreadable. */
export async function loadCollisionVolumes(
  fetchJson: (url: string) => Promise<Response> = (url) => fetch(url),
): Promise<CollisionVolumeTable> {
  try {
    const response = await fetchJson(COLLISION_VOLUMES_URL);
    if (!response.ok) return EMPTY;
    return collisionVolumeTable(await response.json());
  } catch {
    return EMPTY;
  }
}
